import { QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import Products from 'merchandise-lib/constants/Product';
import VersionNumbers from 'self-service-api/constants/VersionNumbers';
import { validateMultipleOfferPurchseConfig, validatePurchaseConfig, validatePurchaseConfigV2 } from 'self-service-api/core/validators';
import { RECURRING } from '../constants/ChargeTypes';
import offerBuilderApi from '../core/api/offerBuilderApi';
import quantityUpgradesApi from '../core/api/quantityUpgradesApi';
import { createOfferPayload } from '../core/utilities/createOfferPayload';
import { createQuantityUpgradesPayload } from '../core/utilities/createQuantityUpgradesPayload';
import { getProductApiNameMap, getProductInfoFromSkuId, getProductInfoFromSkuIdV2, getProductMap, getSkuMap, getSkuMapV2 } from '../core/utilities/productUtils';
import { isCoreSeatSku, isQuantifiableSalesOrServiceSeatSku, isQuantifiableSalesOrServiceSeatSkuV2 } from '../core/utilities/seatUtils';
import { getIsSkuOwned } from './getIsProductOwned';
import { isCoreSeat, isProductVersion } from '../core/TypeGuards';
const isQuantityPackSkuId = (skuId, skuMap) => {
  const skuType = skuMap[skuId].skuType;
  return skuType === QUANTITY_PACK;
};

/**
 * Consider using getPurchaseOrderIdV2 instead, which uses the client types from growth-monetization-service-types.
 */
const getIsQuantityUpgrade = ({
  productConfigurations
}, products, skuMap) => {
  if (!productConfigurations || !productConfigurations.length) {
    return Promise.resolve(false);
  }
  const isQuantityUpgradePromises = productConfigurations.map(({
    skuId
  }) => {
    // Add special case for sales starter and sales pro legacy additional seat purchase
    const productInfo = getProductInfoFromSkuId(products, skuId);
    const versionNumber = skuMap[skuId].versionNumber;
    if (productInfo && productInfo.apiName === Products.SALES_HUB_STARTER && versionNumber === VersionNumbers[Products.SALES_HUB_STARTER].LEGACY) {
      return getIsSkuOwned(skuId);
    }
    if (productInfo && productInfo.apiName === Products.SALES_HUB_PROFESSIONAL && versionNumber === VersionNumbers[Products.SALES_HUB_PROFESSIONAL].LEGACY_BUSINESS_USER) {
      return getIsSkuOwned(skuId);
    }

    // Update for Seats -> treat additional core seat purchases as if they're quantity packs
    if (productInfo && isCoreSeatSku(productInfo.apiName)) {
      return getIsSkuOwned(skuId);
    }

    // Update for Seats -> treat sales/service at pro/ent as if they're quantity packs
    if (productInfo && isQuantifiableSalesOrServiceSeatSku(products, skuMap, skuId)) {
      return getIsSkuOwned(skuId);
    }
    return Promise.resolve(isQuantityPackSkuId(skuId, skuMap));
  });
  return Promise.all(isQuantityUpgradePromises).then(isQuantityUpgrade => {
    return isQuantityUpgrade.every(upgrade => !!upgrade);
  });
};
const getIsQuantityUpgradeV2 = ({
  productConfigurations
}, products, skuMap) => {
  if (!productConfigurations || !productConfigurations.length) {
    return Promise.resolve(false);
  }
  const isQuantityUpgradePromises = productConfigurations.map(({
    skuId
  }) => {
    // Add special case for sales starter and sales pro legacy additional seat purchase
    const productInfo = getProductInfoFromSkuIdV2(products, skuId);
    const sku = skuMap[skuId];
    if (productInfo && productInfo.apiName === Products.SALES_HUB_STARTER && isProductVersion(sku) && sku.versionNumber === VersionNumbers[Products.SALES_HUB_STARTER].LEGACY) {
      return getIsSkuOwned(skuId);
    }
    if (productInfo && productInfo.apiName === Products.SALES_HUB_PROFESSIONAL && isProductVersion(sku) && sku.versionNumber === VersionNumbers[Products.SALES_HUB_PROFESSIONAL].LEGACY_BUSINESS_USER) {
      return getIsSkuOwned(skuId);
    }

    // Update for Seats -> treat additional core seat purchases as if they're quantity packs
    if (productInfo && isCoreSeat(productInfo.apiName)) {
      return getIsSkuOwned(skuId);
    }

    // Update for Seats -> treat sales/service at pro/ent as if they're quantity packs
    if (productInfo && isQuantifiableSalesOrServiceSeatSkuV2(products, skuMap, skuId)) {
      return getIsSkuOwned(skuId);
    }
    return Promise.resolve(isQuantityPackSkuId(skuId, skuMap));
  });
  return Promise.all(isQuantityUpgradePromises).then(isQuantityUpgrade => {
    return isQuantityUpgrade.every(upgrade => !!upgrade);
  });
};

/**
 * @deprecated Use getPurchaseOrderIdV2. productMap uses productId which will be deprecated in favor of productApiName.
 */
const getPurchaseOrderId = (purchaseConfig, products) => {
  const skuMap = getSkuMap(products);
  const productMap = getProductMap(products);
  validatePurchaseConfig(purchaseConfig, productMap, skuMap);
  const {
    offerId,
    currencyCode,
    productConfigurations,
    productBasedOffers = []
  } = purchaseConfig;
  return getIsQuantityUpgrade(purchaseConfig, products, skuMap).then(isQuantityUpgrade => {
    if (isQuantityUpgrade) {
      const quantityUpgradesPayload = createQuantityUpgradesPayload(productConfigurations, products);
      return quantityUpgradesApi.post(quantityUpgradesPayload);
    }
    const offerPayload = createOfferPayload({
      offerId,
      productConfigurations,
      productBasedOffers,
      products,
      requestedCurrency: currencyCode
    });
    return offerBuilderApi.post({
      offerPayload
    }).then(response => response.id);
  });
};
export const getPurchaseOrderIdV2 = (purchaseConfig, products) => {
  const skuMap = getSkuMapV2(products);
  const productMap = getProductApiNameMap(products);
  validatePurchaseConfigV2(purchaseConfig, productMap, skuMap);
  const {
    offerId,
    currencyCode,
    productConfigurations,
    productBasedOffers = []
  } = purchaseConfig;
  return getIsQuantityUpgradeV2(purchaseConfig, products, skuMap).then(isQuantityUpgrade => {
    if (isQuantityUpgrade) {
      const quantityUpgradesPayload = createQuantityUpgradesPayload(productConfigurations, products);
      return quantityUpgradesApi.post(quantityUpgradesPayload);
    }
    const offerPayload = createOfferPayload({
      offerId,
      productConfigurations,
      productBasedOffers,
      products,
      requestedCurrency: currencyCode
    });
    return offerBuilderApi.post({
      offerPayload
    }).then(response => response.id);
  });
};
const getIsMultipleOfferQuantityUpgrade = ({
  offerProductConfigurations
}, products, skuMap) => {
  if (!offerProductConfigurations || !offerProductConfigurations.length) {
    return Promise.resolve(false);
  }
  const skuIdsInConfig = offerProductConfigurations.map(({
    productConfigurations
  }) => productConfigurations).flat().map(({
    skuId
  }) => skuId);
  const isQuantityUpgradePromises = skuIdsInConfig.map(skuId => {
    // Add special case for sales starter and sales pro legacy additional seat purchase
    const productInfo = getProductInfoFromSkuId(products, skuId);
    const productVersion = productInfo && productInfo.productVersions && productInfo.productVersions.find(version => version.skuId === skuId);
    const versionNumber = skuMap[skuId].versionNumber;
    if (productInfo && productInfo.apiName === Products.SALES_HUB_STARTER && versionNumber === VersionNumbers[Products.SALES_HUB_STARTER].LEGACY) {
      return getIsSkuOwned(skuId);
    }
    if (productInfo && productInfo.apiName === Products.SALES_HUB_PROFESSIONAL && versionNumber === VersionNumbers[Products.SALES_HUB_PROFESSIONAL].LEGACY_BUSINESS_USER) {
      return getIsSkuOwned(skuId);
    }

    // Update for Seats -> treat additional core seat purchases as if they're quantity packs
    if (productInfo && isCoreSeatSku(productInfo.apiName)) {
      return getIsSkuOwned(skuId);
    }

    // Update for Seats -> treat sales/service at pro/ent as if they're quantity packs
    if (productInfo && isQuantifiableSalesOrServiceSeatSku(products, skuMap, skuId)) {
      return getIsSkuOwned(skuId);
    }

    // Recurring quantifiable skus
    if (productInfo && productInfo.chargeType === RECURRING && productVersion && productVersion.quantifiable) {
      return getIsSkuOwned(skuId);
    }
    return Promise.resolve(isQuantityPackSkuId(skuId, skuMap));
  });
  return Promise.all(isQuantityUpgradePromises).then(isQuantityUpgrade => {
    return isQuantityUpgrade.every(upgrade => !!upgrade);
  });
};
export const getMultipleOfferPurchaseOrderId = (purchaseConfig, products) => {
  const skuMap = getSkuMap(products);
  validateMultipleOfferPurchseConfig(purchaseConfig);
  const {
    offerProductConfigurations,
    nonRecurringProductConfigurations,
    currencyCode,
    productBasedOffers = []
  } = purchaseConfig;
  return getIsMultipleOfferQuantityUpgrade(purchaseConfig, products, skuMap).then(isQuantityUpgrade => {
    if (isQuantityUpgrade) {
      const quantityUpgradesPayload = offerProductConfigurations.map(({
        productConfigurations: configs
      }) => configs).flat();
      return quantityUpgradesApi.post(quantityUpgradesPayload);
    }
    const offerPayload = {
      nonRecurringProductConfigurations,
      requestedCurrency: currencyCode,
      offerProductConfigurations: [...offerProductConfigurations, ...productBasedOffers.map(productBasedOfferId => ({
        offerId: productBasedOfferId,
        productConfigurations: []
      }))]
    };
    return offerBuilderApi.post({
      offerPayload
    }).then(response => response.id);
  });
};
export default getPurchaseOrderId;